import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { AxiosError } from "axios";

import { testsService } from "@/services/tests";
import { Test } from "@/types/test";

export type LatestReadyTestContextType = {
  vaginalTest?: Test;
  urineTest?: Test;
  currentVaginalTest?: Test;
  currentUrineTest?: Test;
  setCurrentVaginalTest: (currentVaginalTest: Test) => void;
  setCurrentUrineTest: (currentUrineTest: Test) => void;
  loading: boolean;
  eligibleForAnyCare: boolean;
  eligibleForVaginitisCare: boolean;
  eligibleForVaginitisCareReason: string;
  eligibleForVaginitisALaCare: boolean;
  eligibleForUngatedRx: boolean;
  eligibleForHealthHistoryUpsell: boolean;
  vaginitisCarePurchased: boolean;
  userHasNewInProgressVaginalTest: boolean;
  userHasNewInProgressUrineTest: boolean;
};

export const LatestReadyTestContext = createContext<LatestReadyTestContextType>(
  {
    setCurrentVaginalTest: () => {},
    setCurrentUrineTest: () => {},
    loading: false,
    eligibleForAnyCare: false,
    eligibleForVaginitisCare: false,
    eligibleForVaginitisCareReason: "",
    eligibleForVaginitisALaCare: false,
    eligibleForUngatedRx: false,
    eligibleForHealthHistoryUpsell: false,
    vaginitisCarePurchased: false,
    userHasNewInProgressVaginalTest: false,
    userHasNewInProgressUrineTest: false,
  }
);

// Hook to consume the context
export const useLatestReadyTestContext = () => {
  const context = useContext(LatestReadyTestContext);
  if (!context) {
    throw new Error(
      "useLatestReadyTestContext must be used within a LatestReadyTestContextProvider"
    );
  }
  return context;
};

export const LatestReadyTestContextProvider = ({
  isAuthenticated,
  children,
}: {
  isAuthenticated: boolean;
  children: ReactNode;
}) => {
  const [vaginalTest, setVaginalTest] = useState<Test>();
  const [urineTest, setUrineTest] = useState<Test>();
  const [currentVaginalTest, setCurrentVaginalTest] = useState<Test>();
  const [currentUrineTest, setCurrentUrineTest] = useState<Test>();
  const [loading, setLoading] = useState(true);

  const careEligibility = useMemo(() => {
    const eligibleForVaginitisCare = vaginalTest?.eligible_for_care ?? false;
    const eligibleForVaginitisCareReason =
      vaginalTest?.eligible_for_care_reason ?? "";
    const eligibleForVaginitisALaCare =
      vaginalTest?.eligible_for_a_la_care ?? false;
    // eligibleForAnyCare should consider both vaginal and urine tests
    const eligibleForAnyCare =
      (vaginalTest?.eligible_for_any_vaginitis_care ?? false) ||
      (urineTest?.eligible_for_uti_care ?? false);
    const eligibleForUngatedRx = vaginalTest?.eligible_for_ungated_rx ?? false;
    const eligibleForHealthHistoryUpsell =
      !!currentVaginalTest?.healthcontext?.health_history_submitted_at;
    const vaginitisCarePurchased = currentVaginalTest?.care_purchased ?? false;
    const userHasNewInProgressVaginalTest =
      vaginalTest?.hash !== currentVaginalTest?.hash;
    const userHasNewInProgressUrineTest =
      urineTest?.hash !== currentUrineTest?.hash;

    return {
      eligibleForVaginitisCare,
      eligibleForVaginitisCareReason,
      eligibleForVaginitisALaCare,
      eligibleForAnyCare,
      eligibleForUngatedRx,
      eligibleForHealthHistoryUpsell,
      vaginitisCarePurchased,
      userHasNewInProgressVaginalTest,
      userHasNewInProgressUrineTest,
    };
  }, [vaginalTest, urineTest, currentVaginalTest, currentUrineTest]);

  useEffect(() => {
    const fetchTests = async () => {
      setLoading(true);
      // Settled separately: latest_ready 404s when no test is ready, which must not
      // discard the current (in-progress) test.
      const [latestReady, current] = await Promise.allSettled([
        testsService.asyncFetchLatestReadyTest(),
        testsService.fetchCurrentTest(),
      ]);
      if (latestReady.status === "fulfilled" && latestReady.value?.data) {
        setVaginalTest(latestReady.value.data.vaginal_test);
        setUrineTest(latestReady.value.data.urine_test);
      }
      if (current.status === "fulfilled" && current.value?.data) {
        setCurrentVaginalTest(current.value.data.vaginal_test);
        setCurrentUrineTest(current.value.data.urine_test);
      }
      for (const result of [latestReady, current]) {
        if (
          result.status === "rejected" &&
          (result.reason as AxiosError)?.response?.status !== 404
        ) {
          console.error(result.reason);
        }
      }
      setLoading(false);
    };

    if (isAuthenticated) {
      fetchTests();
    } else {
      // Clear stale data and set loading to false since no fetch is needed
      setVaginalTest(undefined);
      setUrineTest(undefined);
      setCurrentVaginalTest(undefined);
      setCurrentUrineTest(undefined);
      setLoading(false);
    }
  }, [isAuthenticated]);

  return (
    <LatestReadyTestContext.Provider
      value={{
        vaginalTest,
        urineTest,
        currentVaginalTest,
        currentUrineTest,
        setCurrentVaginalTest,
        setCurrentUrineTest,
        loading,
        ...careEligibility,
      }}
    >
      {children}
    </LatestReadyTestContext.Provider>
  );
};
